import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from '../shared/Toast';
import { hapticSuccess, hapticTick } from '../../_utils/haptics';
import {
  useDiscountCodes, me, canEdit, canManageCodes, setStatus, loadChoices, answerOf,
} from './discountStore';
import {
  blankCode, dateSaid, daysSaid, draftFromIdea, headlineOf, IDEAS, money, stateOf, usesOf, suggestCode,
} from './data/codes';
import { Icon, MoreMenu, Pill, ConfirmSheet } from './ui';
import CodeEditor from './CodeEditor';
import ShareSheet from './ShareSheet';
import DiscountsHelp from './DiscountsHelp';
import '../marketing/campaigns/Campaigns.css';
import './discounts.css';

/**
 * Marketing › Discounts: every code at this location. The salon's codes (or,
 * for a self-employed stylist, their own) with figures, share and edit; a
 * stylist's codes are shown to the owner, and the salon's to a stylist, so
 * nobody is surprised by a code on their services.
 */

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'live', label: 'Live' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'paused', label: 'Paused' },
  { id: 'ended', label: 'Ended' },
];
const ORDER = { live: 0, scheduled: 1, paused: 2, 'used-up': 3, ended: 4 };
const tabOf = (state) => (state === 'used-up' ? 'ended' : state);

const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || '';

// One line under the headline: dates, who it's for, anything unusual
export const metaOf = (code, now = new Date()) => {
  const st = stateOf(code, now);
  const bits = [];
  if (st === 'scheduled') bits.push(`Starts ${dateSaid(code.startsOn)}`);
  if (st === 'ended') bits.push(code.endedOn ? `Ended ${dateSaid(code.endedOn)}` : code.endsOn ? `Ended ${dateSaid(code.endsOn)}` : 'Ended');
  else if (st === 'used-up') bits.push('Every use taken');
  else bits.push(code.endsOn ? `Book by ${dateSaid(code.endsOn)}` : 'No end date');
  if (code.audience === 'new') bits.push('New clients');
  const d = daysSaid(code.days);
  if (d) bits.push(d);
  if (code.minSpend) bits.push(`Min spend ${money(code.minSpend)}`);
  const ch = code.channels || {};
  if (ch.online && !ch.salon) bits.push('Online only');
  if (!ch.online && ch.salon) bits.push('In person only');
  return bits.join(' · ');
};

const sortCodes = (list, now) => [...list].sort((a, b) => {
  const sa = stateOf(a, now);
  const sb = stateOf(b, now);
  if (ORDER[sa] !== ORDER[sb]) return ORDER[sa] - ORDER[sb];
  if (sa === 'live') return String(a.endsOn || '9999').localeCompare(String(b.endsOn || '9999'));
  if (sa === 'scheduled') return String(a.startsOn || '').localeCompare(String(b.startsOn || ''));
  return String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''));
});

// How a salon code sits with a self-employed stylist's own answer
const coverageFor = (code, answer) => {
  if (answer === null || answer === undefined) return { tone: 'wait', text: 'Not on your services until you choose.' };
  if (answer === 'no') return { tone: 'no', text: 'Not on your services. You said no to the owner’s codes.' };
  if (code.fundingMode === 'salon') return { tone: 'yes', text: 'Works on your services. The business owner pays for it.' };
  if (answer === 'shared') return { tone: 'yes', text: 'Works on your services. The cost is shared in line with your split.' };
  return { tone: 'no', text: 'Not on your services. It’s shared, and you said only when the business owner pays.' };
};

function CodeCard({ code, now, mine, ownerLabel, coverage, onOpen, onShare, onStatus, onCopy }) {
  const st = stateOf(code, now);
  const uses = usesOf(code);
  const cap = Number(code.totalUsesCap) || 0;
  const s = code.stats || {};
  const live = st === 'live' || st === 'scheduled';
  const open = mine ? () => { hapticTick(); onOpen(code); } : undefined;
  const items = mine ? [
    { label: lockedLabel(code), onClick: () => onOpen(code) },
    live && { label: 'Share', onClick: () => onShare(code) },
    st === 'live' && { label: 'Pause', onClick: () => onStatus(code, 'paused') },
    st === 'paused' && { label: 'Turn back on', onClick: () => onStatus(code, 'live') },
    { label: 'Make a copy', onClick: () => onCopy(code) },
    st !== 'ended' && st !== 'used-up' && { label: 'End now', danger: true, onClick: () => onStatus(code, 'ended') },
  ] : [];
  return (
    <article
      className={`dc-card${mine ? ' dc-card--tap' : ''}`}
      onClick={open}
      onKeyDown={mine ? (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(); } : undefined}
      tabIndex={mine ? 0 : undefined}
      aria-label={mine ? `${code.code}, ${headlineOf(code)}. Open` : undefined}
    >
      <div className="dc-card-top">
        <span className="dc-code">{code.code}</span>
        {ownerLabel && <span className="dc-pill dc-pill--owner">{ownerLabel}</span>}
        <span className="dc-spacer" />
        <Pill state={st} />
      </div>
      <h3 className="dc-card-head">{headlineOf(code)}</h3>
      <p className="dc-card-meta">{metaOf(code, now)}</p>
      {coverage && <p className="dc-card-cover"><span className={`dc-tag dc-tag--${coverage.tone}`}>{coverage.tone === 'yes' ? 'On your services' : coverage.tone === 'wait' ? 'Waiting for you' : 'Not on yours'}</span> {coverage.text}</p>}
      {(cap || uses > 0) && (
        <div className="dc-card-use">
          <div className="dc-card-use-row">
            <span>{uses ? <><b>{uses}</b>{cap ? ` of ${cap}` : ''} {uses === 1 && !cap ? 'use' : 'used'}</> : `0 of ${cap} used`}</span>
            {s.newClients ? <span><b>{s.newClients}</b> new {s.newClients === 1 ? 'client' : 'clients'}</span> : null}
          </div>
          {cap ? <div className="dc-meter" aria-hidden="true"><i style={{ width: `${Math.min(100, (uses / cap) * 100)}%` }} /></div> : null}
        </div>
      )}
      {(mine || uses > 0) && (
        <div className="dc-card-foot">
          <span className="dc-card-figs">{uses ? `${money(s.revenue || 0)} taken · ${money(s.discountGiven || 0)} off` : (cap ? '' : 'Not used yet')}</span>
          <span className="dc-spacer" />
          {mine && live && (
            <button type="button" className="dc-btn" onClick={(e) => { e.stopPropagation(); hapticTick(); onShare(code); }}>
              <Icon.Share size={15} />Share
            </button>
          )}
          {mine && <MoreMenu items={items} label={`More for ${code.code}`} />}
        </div>
      )}
    </article>
  );
}

const lockedLabel = (code) => (usesOf(code) > 0 ? 'See details' : 'Edit');

export default function DiscountsContent({ addSignal = 0, helpSignal = 0, newFor = null }) {
  const now = useMemo(() => new Date(), []);
  const { codes, loaded, source, error, reload } = useDiscountCodes();
  const who = me();
  const [tab, setTab] = useState('all');
  const [editing, setEditing] = useState(null); // { draft }
  const [sharing, setSharing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { code, status }
  const [helpOpen, setHelpOpen] = useState(false);
  const [myAnswer, setMyAnswer] = useState(undefined);
  const allowed = canManageCodes();

  useEffect(() => { if (addSignal) openNew(); }, [addSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (helpSignal) setHelpOpen(true); }, [helpSignal]);
  // From a service in Catalogue: a new code for that service
  useEffect(() => {
    if (!newFor || !newFor.id || !canManageCodes()) return;
    const b = base();
    openNew({ scope: { ...b.scope, services: 'some', serviceIds: [newFor.id], names: { [newFor.id]: newFor.name || 'This service' } } });
    // Spent: a reload doesn't open it again
    try { window.history.replaceState({}, document.title, window.location.pathname); } catch (e) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!who.selfEmployed) return;
    loadChoices().then((c) => setMyAnswer(answerOf(c[String(who.userId)]))).catch(() => setMyAnswer(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const base = () => blankCode(who.selfEmployed
    ? { ownerType: 'stylist', ownerId: who.userId, ownerName: who.name, salonId: who.salonId }
    : { ownerType: 'salon', ownerId: who.salonId, salonId: who.salonId }, new Date());

  function openNew(patch) {
    const d = { ...base(), ...(patch || {}) };
    if (!d.code) d.code = suggestCode(d, codes);
    setEditing({ draft: d });
  }
  const openIdea = (idea) => { hapticTick(); setEditing({ draft: draftFromIdea(idea, base(), codes, new Date()) }); };
  const openCode = (code) => setEditing({ draft: code });
  const copyCode = (code) => {
    const { id, stats, history, createdAt, createdBy, updatedAt, endedOn, ...rest } = code; // eslint-disable-line no-unused-vars
    const d = { ...rest, status: 'live', startsOn: base().startsOn, code: '' };
    d.code = suggestCode(d, codes);
    setEditing({ draft: d });
  };

  const askStatus = (code, status) => {
    if (status === 'live') {
      setStatus(code, 'live').then(() => { hapticSuccess(); toast.success(`${code.code} is back on`); }).catch((e) => toast.error((e && e.message) || 'That didn’t save. Try again.'));
      return;
    }
    setConfirm({ code, status });
  };

  const doStatus = (note) => {
    const { code, status } = confirm;
    setConfirm(null);
    setStatus(code, status, note).then(() => {
      hapticSuccess();
      toast.success(status === 'paused' ? `${code.code} is paused` : `${code.code} has ended`);
    }).catch((e) => toast.error((e && e.message) || 'That didn’t save. Try again.'));
  };

  // Mine (what I manage) and the others (shown so nobody is surprised)
  const mineList = codes.filter((c) => (who.selfEmployed ? c.ownerType === 'stylist' && String(c.ownerId) === String(who.userId) : c.ownerType !== 'stylist'));
  const others = who.selfEmployed
    ? codes.filter((c) => c.ownerType !== 'stylist' && ['live', 'scheduled'].includes(stateOf(c, now)) && (!(c.scope && c.scope.stylistIds && c.scope.stylistIds.length) || c.scope.stylistIds.map(String).includes(String(who.userId))))
    : codes.filter((c) => c.ownerType === 'stylist' && ['live', 'scheduled', 'paused'].includes(stateOf(c, now)));
  const counts = TABS.reduce((acc, t) => ({ ...acc, [t.id]: t.id === 'all' ? mineList.length : mineList.filter((c) => tabOf(stateOf(c, now)) === t.id).length }), {});
  const shown = sortCodes(tab === 'all' ? mineList : mineList.filter((c) => tabOf(stateOf(c, now)) === tab), now);

  const empty = loaded && mineList.length === 0;

  return (
    <div className="dc-page">
      <div className="cmp-wrap" style={{ paddingTop: 28 }}>
        <div className="cmp-head">
          <div className="cmp-head-text">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 className="cmp-title">Discount codes</h1>
              <button type="button" className="pv2-help-btn" onClick={() => setHelpOpen(true)} aria-label="How discount codes work" title="How discount codes work">?</button>
            </div>
            <p className="cmp-subtitle">
              {who.selfEmployed
                ? 'Codes for your own clients, on your own services. Share them in posts, emails and texts, or as a link or QR code.'
                : 'Codes clients can use when they book online or pay in person. Share them in posts, emails and texts, or as a link or QR code.'}
            </p>
          </div>
        </div>

        {source === 'standin' && (
          <div className="dc-standin" role="note">
            <Icon.Info size={16} />
            <span><b>Test version.</b> Codes are saved on this device until the server side is ready, so they work here and on this device’s booking page.</span>
          </div>
        )}

        {who.selfEmployed && (
          <div className="dc-choice-row">
            <p><b>The owner’s codes on your services:</b> {myAnswer === undefined ? '…' : myAnswer === 'shared' ? 'yes, and you’ll share the cost.' : myAnswer === 'salon' ? 'only when the business owner pays.' : myAnswer === 'no' ? 'no.' : 'you haven’t chosen yet, so they don’t work on your services.'}</p>
            <Link className="dc-btn" to="/settings/your-choices">{myAnswer ? 'Change' : 'Choose'}</Link>
          </div>
        )}

        {!loaded && <div className="dc-none" aria-busy="true">Loading your codes…</div>}
        {loaded && error && !codes.length && (
          <div className="dc-none">
            <p style={{ margin: '0 0 12px' }}>{error}</p>
            <button type="button" className="dc-btn" onClick={reload}>Try again</button>
          </div>
        )}

        {empty && !error && (
          <>
            <div className="dc-empty">
              <span className="dc-empty-icon"><Icon.Ticket size={28} /></span>
              <h2>Make your first code</h2>
              <p>{who.selfEmployed ? 'A code takes money off your services for the clients you give it to. You choose what it’s worth, who it’s for and when it ends.' : 'A code takes money off for the clients you give it to. You choose what it’s worth, what it covers, who it’s for and when it ends.'}</p>
            </div>
            {allowed && (
              <>
                <div className="dc-ideas">
                  {IDEAS.map((idea) => {
                    const d = draftFromIdea(idea, base(), codes, now);
                    return (
                      <button key={idea.id} type="button" className="dc-idea" onClick={() => openIdea(idea)}>
                        <span className="dc-code">{d.code}</span>
                        <b>{idea.title}</b>
                        <span>{idea.said}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="dc-scratch">
                  <button type="button" className="dc-btn" onClick={() => { hapticTick(); openNew(); }}><Icon.Plus size={15} />Start from scratch</button>
                </div>
              </>
            )}
          </>
        )}

        {loaded && mineList.length > 0 && (
          <>
            <div className="dc-toolbar">
              <div className="cmp-filter" role="tablist" aria-label="Show codes">
                {TABS.map((t) => (
                  <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`cmp-filter-tab${tab === t.id ? ' active' : ''}`} onClick={() => { hapticTick(); setTab(t.id); }}>
                    {t.label}<span className="cmp-filter-count">{counts[t.id]}</span>
                  </button>
                ))}
              </div>
            </div>
            {shown.length ? (
              <div className="dc-grid">
                {shown.map((c) => (
                  <CodeCard key={c.id} code={c} now={now} mine={canEdit(c, who)} onOpen={openCode} onShare={setSharing} onStatus={askStatus} onCopy={copyCode} />
                ))}
              </div>
            ) : (
              <div className="dc-none">{tab === 'live' ? 'No codes are live right now.' : tab === 'scheduled' ? 'Nothing scheduled.' : tab === 'paused' ? 'No paused codes.' : 'No ended codes yet.'}</div>
            )}
          </>
        )}

        {loaded && others.length > 0 && (
          <>
            <h2 className="dc-section-title" style={{ marginTop: mineList.length ? 36 : 28 }}>{who.selfEmployed ? 'The business owner’s codes' : 'Self-employed stylists’ own codes'}</h2>
            <p className="dc-section-note">{who.selfEmployed ? 'Codes the business owner runs. Whether they work on your services is up to you.' : 'Codes stylists run on their own services and pay for themselves. Only they can change them.'}</p>
            <div className="dc-grid">
              {sortCodes(others, now).map((c) => (
                <CodeCard
                  key={c.id}
                  code={c}
                  now={now}
                  mine={false}
                  ownerLabel={c.ownerType === 'stylist' ? `${firstName(c.ownerName) || 'Stylist'}’s` : null}
                  coverage={who.selfEmployed ? coverageFor(c, myAnswer) : null}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {editing && (
        <CodeEditor
          draft={editing.draft}
          codes={codes}
          onClose={() => setEditing(null)}
          onSaved={(saved, isNew) => {
            setEditing(null);
            if (isNew) { setTab('all'); setSharing(saved); }
          }}
          onShare={(c) => { setEditing(null); setSharing(c); }}
          onStatus={(c, s) => { setEditing(null); askStatus(c, s); }}
        />
      )}
      {sharing && <ShareSheet code={sharing} onClose={() => setSharing(null)} />}
      {confirm && (
        <ConfirmSheet
          title={confirm.status === 'paused' ? `Pause ${confirm.code.code}?` : `End ${confirm.code.code} now?`}
          text={confirm.status === 'paused'
            ? 'Clients can’t use it while it’s paused. Bookings already made keep their price. You can turn it back on any time.'
            : 'Clients can’t use it after this. Bookings already made keep their price. You can’t turn an ended code back on.'}
          confirm={confirm.status === 'paused' ? 'Pause code' : 'End code'}
          danger={confirm.status === 'ended'}
          reason={{ label: 'Reason (optional, kept in its history)', placeholder: confirm.status === 'paused' ? 'For example, fully booked this week' : 'For example, the offer has finished' }}
          onCancel={() => setConfirm(null)}
          onConfirm={doStatus}
        />
      )}
      {helpOpen && <DiscountsHelp selfEmployed={who.selfEmployed} onClose={() => setHelpOpen(false)} />}
    </div>
  );
}
